import { zeroErrorMessage } from "@/lib/zero-error";
import { useState } from "react";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { appMutators, appQueries } from "@production-hub/shared";
import { Tables } from "@/integrations/neon/types";
import { generateUUID } from "@/lib/utils";
import { toast } from "sonner";

export type PersonSkillRating = Tables<"person_skill_ratings"> & {
  default_function?: {
    id: string;
    name: string;
  };
};

export type PersonSkillRatingWithFunction = {
  id: string;
  person_id: string;
  default_function_id: string;
  rating: number;
  notes: string | null;
  default_function: {
    id: string;
    name: string;
  };
};

type ZeroSkillRatingRow = {
  id: string;
  personID: string;
  defaultFunctionID: string;
  rating: number;
  notes?: string | null;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
  defaultFunction?: {
    id: string;
    name: string;
  } | null;
};

type MutationOptions<T> = { onSuccess?: (data: T) => void };
type ZeroMutationResult =
  | { type: "error"; error: { message: string } }
  | { type: string; error?: never };
type ZeroMutateClient = {
  mutate: (mutation: unknown) => {
    client: Promise<void>;
    server: Promise<ZeroMutationResult>;
  };
};

function toLegacyTimestamp(value: number | string | null | undefined) {
  if (typeof value === "number") return new Date(value).toISOString();
  return value ?? "";
}

function toLegacySkillRating(row: ZeroSkillRatingRow): PersonSkillRating {
  return {
    id: row.id,
    person_id: row.personID,
    default_function_id: row.defaultFunctionID,
    rating: row.rating,
    notes: row.notes ?? null,
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
    default_function: row.defaultFunction
      ? { id: row.defaultFunction.id, name: row.defaultFunction.name }
      : undefined,
  };
}

function toLegacySkillRatingWithFunction(
  row: ZeroSkillRatingRow,
): PersonSkillRatingWithFunction {
  return {
    id: row.id,
    person_id: row.personID,
    default_function_id: row.defaultFunctionID,
    rating: row.rating,
    notes: row.notes ?? null,
    default_function: {
      id: row.defaultFunction?.id ?? row.defaultFunctionID,
      name: row.defaultFunction?.name ?? "",
    },
  };
}

async function reportServerMutationFailure(
  serverResult: Promise<ZeroMutationResult>,
  fallback: string,
) {
  const result = await serverResult;
  if (result.type === "error") {
    throw new Error(result.error?.message || fallback);
  }
}

export function usePersonSkillRatings(personId: string | undefined) {
  const query = personId
    ? appQueries.personSkillRatings.byPerson({ personID: personId })
    : undefined;
  const [data, details] = useQuery(query, { enabled: !!personId });
  const ratings = ((data ?? []) as ZeroSkillRatingRow[]).map(
    toLegacySkillRating,
  );

  return {
    data: ratings,
    isLoading:
      !!personId && details.type !== "complete" && ratings.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useAllSkillRatings() {
  const [data, details] = useQuery(
    appQueries.personSkillRatings.allRatedAtLeast({ minRating: 2 }),
  );
  const ratings = ((data ?? []) as ZeroSkillRatingRow[]).map(
    toLegacySkillRatingWithFunction,
  );

  return {
    data: ratings,
    isLoading: details.type !== "complete" && ratings.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useUpsertSkillRating() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async ({
    personId,
    defaultFunctionId,
    rating,
    notes,
  }: {
    personId: string;
    defaultFunctionId: string;
    rating: number;
    notes?: string | null;
  }) => {
    const input = {
      id: generateUUID(),
      personID: personId,
      defaultFunctionID: defaultFunctionId,
      rating,
      notes: notes ?? null,
    };
    setIsPending(true);
    try {
      const mutation = zero.mutate(
        appMutators.personSkillRatings.upsert(input),
      );
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Skill rating update was rejected",
      );
      return toLegacySkillRating({
        ...input,
        createdAt: "",
        updatedAt: "",
      });
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (
      input: {
        personId: string;
        defaultFunctionId: string;
        rating: number;
        notes?: string | null;
      },
      options?: MutationOptions<PersonSkillRating>,
    ) => {
      void run(input)
        .then((data) => options?.onSuccess?.(data))
        .catch((error) =>
          toast.error("Failed to update skill rating: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useDeleteSkillRating() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async ({ id, personId }: { id: string; personId: string }) => {
    setIsPending(true);
    try {
      const mutation = zero.mutate(
        appMutators.personSkillRatings.delete({ id }),
      );
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Skill rating delete was rejected",
      );
      return personId;
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (
      input: { id: string; personId: string },
      options?: MutationOptions<string>,
    ) => {
      void run(input)
        .then((personId) => options?.onSuccess?.(personId))
        .catch((error) =>
          toast.error("Failed to remove skill rating: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}
